import { Selector } from "@planzone/state";
import { Dependencies } from "@planzone/core";
import {
  Gantt,
  Identifiable,
  List,
  Milestone,
  Project,
  SearchQuery,
  SearchQueryType,
  SearchResult,
  Task,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "@planzone/services";

function sanitize(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function matchProject(project: Identifiable<Project>, { name }: SearchQuery) {
  return new RegExp(sanitize(name), "ig").test(project.name);
}

function searchProjects(
  httpClient: HttpClient,
  selector: Selector,
  query: SearchQuery,
): Promise<SearchResult[]> {
  const useLocalGantt =
    selector.isRetrieveZonesProjectsGanttRequestSuccess() &&
    !query.includeAllProjects;

  return (
    useLocalGantt
      ? Promise.resolve(
          selector
            .getZonesProjectsGantt()
            .projects.filter((project) => matchProject(project, query)),
        )
      : httpClient.sendHttpRequest<Identifiable<Project>[]>({
          endpoint: `/projects/search/by-name?name=${encodeURIComponent(query.name)}&includeAllProjects=${query.includeAllProjects ?? false}`,
          method: HttpMethods.GET,
        })
  ).then((projects) => projects.map((args) => ({ type: "project", args })));
}

function matchTask(task: Identifiable<Task>, { name }: SearchQuery) {
  return new RegExp(sanitize(name), "ig").test(task.name);
}

function extractTasks(
  list: Identifiable<List>,
  project: Identifiable<Project>,
  query: SearchQuery,
): SearchResult[] {
  return [
    ...list.tasks
      .filter((task) => matchTask(task, query))
      .map(
        (task) =>
          ({
            type: "task",
            args: { ...task, project, zone: project.zone },
          }) as SearchResult,
      ),
    ...list.subLists.reduce<SearchResult[]>(
      (results, subList) => [
        ...results,
        ...extractTasks(subList, project, query),
      ],
      [],
    ),
  ];
}

function searchTasks(gantt: Gantt, query: SearchQuery): SearchResult[] {
  return gantt.projects.reduce<SearchResult[]>(
    (results, project) => [
      ...results,
      ...project.lists.reduce<SearchResult[]>(
        (res, list) => [...res, ...extractTasks(list, project, query)],
        [],
      ),
    ],
    [],
  );
}

function matchMilestone(
  milestone: Identifiable<Milestone>,
  { name }: SearchQuery,
) {
  return new RegExp(sanitize(name), "ig").test(milestone.name);
}

function extractMilestones(
  list: Identifiable<List>,
  project: Identifiable<Project>,
  query: SearchQuery,
): SearchResult[] {
  return [
    ...list.milestones
      .filter((milestone) => matchMilestone(milestone, query))
      .map(
        (milestone) =>
          ({
            type: "milestone",
            args: { ...milestone, project, zone: project.zone },
          }) as SearchResult,
      ),
    ...list.subLists.reduce<SearchResult[]>(
      (results, subList) => [
        ...results,
        ...extractMilestones(subList, project, query),
      ],
      [],
    ),
  ];
}

function searchMilestones(gantt: Gantt, query: SearchQuery): SearchResult[] {
  return gantt.projects.reduce<SearchResult[]>(
    (results, project) => [
      ...results,
      ...project.lists.reduce<SearchResult[]>(
        (res, list) => [...res, ...extractMilestones(list, project, query)],
        [],
      ),
    ],
    [],
  );
}

function isInSearch(types: SearchQueryType[], type: SearchQueryType) {
  return !!types.find((t) => t === type);
}

function createSearchEngine(
  httpClient: HttpClient,
  selector: Selector,
): Dependencies["searchEngine"] {
  return {
    search(query) {
      const types = query.types || ["project", "task", "milestone"];

      return Promise.all([
        isInSearch(types, "project")
          ? searchProjects(httpClient, selector, query)
          : Promise.resolve([] as SearchResult[]),
        Promise.resolve(
          isInSearch(types, "task")
            ? searchTasks(selector.getZonesProjectsGantt(), query)
            : ([] as SearchResult[]),
        ),
        Promise.resolve(
          isInSearch(types, "milestone")
            ? searchMilestones(selector.getZonesProjectsGantt(), query)
            : ([] as SearchResult[]),
        ),
      ])
        .then((chunks) => chunks.flat())
        .then((res) =>
          res.slice(
            query.start * query.limit,
            query.start * query.limit + query.limit,
          ),
        );
    },
  };
}

export { createSearchEngine };
